import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Check } from 'lucide-react';
import { useWorkspaceStore } from '../../stores/workspace.js';
import { useUpdateWorkspace } from '../../hooks/useWorkspace.js';
import {
  useSecurityProfiles,
  useCreateSecurityProfile,
  useUpdateSecurityProfile,
  useDeleteSecurityProfile,
  type ProfileInput,
} from '../../hooks/useSecurityProfiles.js';
import { toast } from '../../lib/toast.js';
import type { SecurityProfile } from '@mayksel/types';

// ── Profile Editor ─────────────────────────────────────────────────────────────

const ACTION_OPTIONS = [
  { action: 'export', label: 'Export data' },
  { action: 'import', label: 'Import data' },
  { action: 'delete_company', label: 'Delete companies' },
  { action: 'delete_order', label: 'Delete orders' },
  { action: 'view_financials', label: 'View financial fields' },
];

const ENTITY_FIELD_OPTIONS: { entityType: string; label: string; fields: { key: string; label: string }[] }[] = [
  {
    entityType: 'order', label: 'Orders',
    fields: [
      { key: 'subtotal', label: 'Subtotal' },
      { key: 'tax', label: 'Tax' },
      { key: 'total', label: 'Total' },
      { key: 'priority', label: 'Priority' },
      { key: 'due_date', label: 'Due date' },
      { key: 'assigned_to', label: 'Assigned to' },
    ],
  },
  {
    entityType: 'bucket', label: 'Projects',
    fields: [
      { key: 'value', label: 'Value' },
      { key: 'probability', label: 'Probability' },
      { key: 'expected_close', label: 'Expected close' },
    ],
  },
  {
    entityType: 'line_item', label: 'Line Items',
    fields: [
      { key: 'unit_price', label: 'Unit price' },
      { key: 'total', label: 'Total' },
    ],
  },
];

interface ProfileEditorProps {
  profile?: SecurityProfile | undefined;
  onSave: () => void;
  onCancel: () => void;
}

function ProfileEditor({ profile, onSave, onCancel }: ProfileEditorProps) {
  const { t } = useTranslation('settings');
  const create = useCreateSecurityProfile();
  const update = useUpdateSecurityProfile();
  const [name, setName] = useState(profile?.name ?? '');
  const [description, setDescription] = useState(profile?.description ?? '');
  const [isDefault, setIsDefault] = useState(profile?.isDefault ?? false);

  // Track hidden fields: Set of "entityType:fieldKey"
  const [hiddenFields, setHiddenFields] = useState<Set<string>>(
    () => new Set<string>(profile?.fieldRules.filter(r => !r.isVisible).map(r => `${r.entityType}:${r.fieldKey}`) ?? []),
  );

  // Track disallowed actions
  const [disallowedActions, setDisallowedActions] = useState<Set<string>>(
    () => new Set<string>(profile?.actionRules.filter(r => !r.isAllowed).map(r => r.action) ?? []),
  );

  const toggleField = (key: string) => {
    setHiddenFields(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleAction = (action: string) => {
    setDisallowedActions(prev => {
      const next = new Set(prev);
      if (next.has(action)) next.delete(action); else next.add(action);
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    const input: ProfileInput = {
      name: name.trim(),
      description: description.trim() || undefined,
      isDefault,
      fieldRules: ENTITY_FIELD_OPTIONS.flatMap(g =>
        g.fields.map(f => ({
          entityType: g.entityType,
          fieldKey: f.key,
          isVisible: !hiddenFields.has(`${g.entityType}:${f.key}`),
          isEditable: !hiddenFields.has(`${g.entityType}:${f.key}`),
        })),
      ).filter(r => !r.isVisible), // only store restrictions
      actionRules: ACTION_OPTIONS.map(o => ({
        action: o.action,
        isAllowed: !disallowedActions.has(o.action),
      })).filter(r => !r.isAllowed),
    };

    try {
      if (profile) {
        await update.mutateAsync({ id: profile.id, input });
      } else {
        await create.mutateAsync(input);
      }
      toast.success(t('security.profile_saved'));
      onSave();
    } catch {
      toast.error(t('security.profile_save_error'));
    }
  };

  const isBusy = create.isPending || update.isPending;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div>
          <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-1)' }}>
            {t('security.profile_name_label')}
          </label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Warehouse"
            style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', background: 'var(--color-bg)', color: 'var(--color-text)' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-1)' }}>
            {t('security.profile_desc_label')}
          </label>
          <input
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Optional description"
            style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', background: 'var(--color-bg)', color: 'var(--color-text)' }}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer', fontSize: 'var(--text-sm)' }}>
          <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />
          {t('security.profile_default_label')}
        </label>
      </div>

      {/* Field restrictions */}
      <div>
        <p style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-2)' }}>
          {t('security.field_rules_section')} — hidden fields
        </p>
        {ENTITY_FIELD_OPTIONS.map(group => (
          <div key={group.entityType} style={{ marginBottom: 'var(--space-3)' }}>
            <p style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-1)' }}>{group.label}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {group.fields.map(f => {
                const key = `${group.entityType}:${f.key}`;
                const hidden = hiddenFields.has(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleField(key)}
                    style={{
                      padding: 'var(--space-1) var(--space-2)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--text-xs)',
                      border: '1px solid var(--color-border)',
                      background: hidden ? 'var(--color-danger-pale)' : 'var(--color-bg-subtle)',
                      color: hidden ? 'var(--color-danger)' : 'var(--color-text-secondary)',
                      cursor: 'pointer',
                      textDecoration: hidden ? 'line-through' : 'none',
                    }}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Action restrictions */}
      <div>
        <p style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-2)' }}>
          {t('security.action_rules_section')} — blocked actions
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {ACTION_OPTIONS.map(o => {
            const blocked = disallowedActions.has(o.action);
            return (
              <button
                key={o.action}
                type="button"
                onClick={() => toggleAction(o.action)}
                style={{
                  padding: 'var(--space-1) var(--space-2)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--text-xs)',
                  border: '1px solid var(--color-border)',
                  background: blocked ? 'var(--color-danger-pale)' : 'var(--color-bg-subtle)',
                  color: blocked ? 'var(--color-danger)' : 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  textDecoration: blocked ? 'line-through' : 'none',
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={{ padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', background: 'var(--color-bg)', fontSize: 'var(--text-sm)', cursor: 'pointer', color: 'var(--color-text)' }}>
          Cancel
        </button>
        <button
          type="button"
          disabled={!name.trim() || isBusy}
          onClick={() => { void handleSave(); }}
          style={{ padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--color-brand)', color: 'white', fontSize: 'var(--text-sm)', cursor: 'pointer', opacity: isBusy ? 0.6 : 1 }}
        >
          {isBusy ? t('security.profile_saving') : t('security.profile_save')}
        </button>
      </div>
    </div>
  );
}

// ── Main Security Settings Page ───────────────────────────────────────────────

export default function SecuritySettings() {
  const { t } = useTranslation('settings');
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  const workspace = useWorkspaceStore((s) => s.workspace);
  const updateWorkspace = useUpdateWorkspace();
  const { data: profiles = [], isLoading } = useSecurityProfiles();
  const deleteProfile = useDeleteSecurityProfile();

  const [mfaPolicy, setMfaPolicy] = useState(workspace?.mfaPolicy ?? 'optional');
  const [editingProfile, setEditingProfile] = useState<SecurityProfile | 'new' | null>(null);

  const handleSaveMfa = async () => {
    if (!workspaceId) return;
    try {
      await updateWorkspace.mutateAsync({ mfaPolicy });
      toast.success(t('security.mfa_saved'));
    } catch {
      toast.error(t('security.mfa_save_error'));
    }
  };

  const handleDeleteProfile = async (profile: SecurityProfile) => {
    if (!confirm(t('security.profile_delete_confirm', { name: profile.name }))) return;
    try {
      await deleteProfile.mutateAsync(profile.id);
    } catch {
      toast.error(t('security.profile_delete_error'));
    }
  };

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-normal)', marginBottom: 'var(--space-1)' }}>
          {t('security.title')}
        </h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
          {t('security.subtitle')}
        </p>
      </div>

      {/* MFA Policy */}
      <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        <div style={{ padding: 'var(--space-4)', borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-subtle)' }}>
          <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>{t('security.mfa_section')}</p>
        </div>
        <div style={{ padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-1)' }}>
            {t('security.mfa_policy_label')}
          </label>
          {(['optional', 'required_all', 'required_admins'] as const).map(v => (
            <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer', fontSize: 'var(--text-sm)' }}>
              <input type="radio" name="mfa" value={v} checked={mfaPolicy === v} onChange={() => setMfaPolicy(v)} />
              {t(`security.mfa_${v}` as Parameters<typeof t>[0])}
            </label>
          ))}
          <div style={{ marginTop: 'var(--space-2)' }}>
            <button
              type="button"
              onClick={() => { void handleSaveMfa(); }}
              disabled={updateWorkspace.isPending}
              style={{ padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--color-brand)', color: 'white', fontSize: 'var(--text-sm)', cursor: 'pointer', opacity: updateWorkspace.isPending ? 0.6 : 1 }}
            >
              {t('security.mfa_save')}
            </button>
          </div>
        </div>
      </div>

      {/* Security Profiles */}
      <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        <div style={{ padding: 'var(--space-4)', borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>{t('security.profiles_section')}</p>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', marginTop: 2 }}>{t('security.profiles_subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => setEditingProfile('new')}
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--color-brand)', color: 'white', fontSize: 'var(--text-sm)', cursor: 'pointer' }}
          >
            <Plus size={14} />{t('security.new_profile')}
          </button>
        </div>

        <div style={{ padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {editingProfile && (
            <div style={{ border: '1px solid var(--color-brand-light)', borderRadius: 'var(--radius-md)', padding: 'var(--space-4)', background: 'var(--color-brand-pale)', marginBottom: 'var(--space-2)' }}>
              <ProfileEditor
                profile={editingProfile === 'new' ? undefined : editingProfile}
                onSave={() => setEditingProfile(null)}
                onCancel={() => setEditingProfile(null)}
              />
            </div>
          )}

          {isLoading && (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', padding: 'var(--space-4)' }}>Loading…</p>
          )}

          {!isLoading && profiles.length === 0 && !editingProfile && (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', padding: 'var(--space-4)' }}>{t('security.no_profiles')}</p>
          )}

          {profiles.map(profile => (
            <div
              key={profile.id}
              style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-4)' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 4 }}>
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>{profile.name}</span>
                    {profile.isDefault && (
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 'var(--radius-full)', background: 'var(--color-brand-pale)', color: 'var(--color-brand)', fontWeight: 'var(--font-medium)' }}>
                        {t('security.profile_default_badge')}
                      </span>
                    )}
                  </div>
                  {profile.description && (
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>{profile.description}</p>
                  )}
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>
                    {t(profile.assignedUserCount === 1 ? 'security.profile_assigned' : 'security.profile_assigned_plural', { count: profile.assignedUserCount })}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                  <button
                    type="button"
                    onClick={() => setEditingProfile(profile)}
                    style={{ padding: 'var(--space-1)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)', background: 'var(--color-bg)', cursor: 'pointer', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center' }}
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => { void handleDeleteProfile(profile); }}
                    style={{ padding: 'var(--space-1)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)', background: 'var(--color-bg)', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex', alignItems: 'center' }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              {/* Rule summary */}
              {(profile.fieldRules.filter(r => !r.isVisible).length > 0 || profile.actionRules.filter(r => !r.isAllowed).length > 0) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)', marginTop: 'var(--space-2)' }}>
                  {profile.fieldRules.filter(r => !r.isVisible).map(r => (
                    <span key={r.id} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 'var(--radius-full)', background: 'var(--color-danger-pale)', color: 'var(--color-danger)' }}>
                      {r.entityType}.{r.fieldKey} hidden
                    </span>
                  ))}
                  {profile.actionRules.filter(r => !r.isAllowed).map(r => (
                    <span key={r.id} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 'var(--radius-full)', background: 'var(--color-warning-pale)', color: 'var(--color-amber)' }}>
                      {r.action} blocked
                    </span>
                  ))}
                </div>
              )}

              {profile.fieldRules.length === 0 && profile.actionRules.length === 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', marginTop: 4 }}>
                  <Check size={12} style={{ color: 'var(--color-success)' }} />
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>No restrictions</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
